import { useState } from "react";
import {
  Button,
  IconButton,
  Input,
  Modal,
  Select,
  Stack,
  Title
} from "@introist/react-foundation/v2";
import { GitBranch } from "lucide-react";
import { Editor } from "@tiptap/react";
import { Tag } from "../../../../../modules/system/tags/useTags";
import { TagSelectorList } from "../../../../../modules/system/tags/TagSelector";
import {
  ConditionalOperator,
  OPERATOR_OPTIONS,
  OPERATORS_REQUIRING_VALUE
} from "../utils/conditionalSerializer";

type Step = "field" | "operator";

interface ConditionalSelectorProps {
  editor?: Editor | null;
  onClose?: () => void;
}

export const ConditionalSelector = ({ editor, onClose }: ConditionalSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("field");
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [operator, setOperator] = useState<ConditionalOperator>("truthy");
  const [value, setValue] = useState<string>("");

  const needsValue = OPERATORS_REQUIRING_VALUE.includes(operator);

  const handleOpen = () => {
    setStep("field");
    setSelectedTag(null);
    setOperator("truthy");
    setValue("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  const handleConfirm = () => {
    if (!selectedTag) return;

    editor?.chain().focus().insertConditionalBlock({
      condition: selectedTag.tag,
      conditionTitle: selectedTag.fullTitle ?? selectedTag.title,
      operator,
      value: needsValue ? value : null
    }).run();

    handleClose();
  };

  const canConfirm = !!selectedTag && (!needsValue || value.trim().length > 0);

  return (
    <>
      <Stack>
        <IconButton icon={<GitBranch size={16} />} onClick={handleOpen} size="large" />
      </Stack>

      <Modal title="Insert conditional block" open={open} onClose={handleClose}>
        <Stack vertical gap="large" style={{ minWidth: "360px" }}>
          {step === "field" && (
            <Stack vertical gap="small">
              <Title variant="bold">Select a field to check</Title>
              <div style={{ maxHeight: "340px", overflow: "auto" }}>
                <TagSelectorList
                  onSelect={(tag: Tag) => {
                    setSelectedTag(tag);
                    setStep("operator");
                  }}
                />
              </div>
            </Stack>
          )}

          {step === "operator" && (
            <Stack vertical gap="medium">
              <Stack vertical gap="xSmall">
                <Title variant="bold">Field</Title>
                <Stack gap="xSmall" alignItems="center">
                  <span
                    style={{
                      color: "var(--palette-foreground-subdued)",
                      fontSize: "var(--font-size-small)"
                    }}
                  >
                    {selectedTag?.fullTitle ?? selectedTag?.title}
                  </span>
                  <Button variant="blended" size="small" onClick={() => setStep("field")}>
                    Change
                  </Button>
                </Stack>
              </Stack>

              <Stack vertical gap="xSmall">
                <Title variant="bold">Condition</Title>
                <Select
                  options={OPERATOR_OPTIONS}
                  value={operator}
                  onSelect={opt => setOperator(opt.key as ConditionalOperator)}
                  placeholder="Select condition..."
                />
              </Stack>

              {needsValue && (
                <Stack vertical gap="xSmall">
                  <Title variant="bold">Value</Title>
                  <Input
                    value={value}
                    onChange={val => setValue(val)}
                    placeholder="Enter value..."
                    autoFocus
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter" && canConfirm) handleConfirm();
                    }}
                  />
                </Stack>
              )}

              <Stack justifyContent="flex-end" gap="small">
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button onClick={handleConfirm} disabled={!canConfirm}>
                  Insert
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Modal>
    </>
  );
};
